import {Injectable, inject} from '@angular/core';
import {
  MappingEngineGateway,
  MappingEngineGetDetailsResp,
  MappingEngineGetReq,
} from '@em/shared/api-interface';
import {
  DestinationAttribute,
  MappingEngine,
  MappingEngineAttributes,
  MappingEngineOwner,
  MappingEngineShopSystem,
} from '@em/shared/mapping-engine/domain-models';
import {ComponentStore} from '@ngrx/component-store';
import {tapResponse} from '@ngrx/operators';
import {combineLatest, Observable} from 'rxjs';
import {filter, switchMap, withLatestFrom} from 'rxjs/operators';
import {BackEndModelBuilderService} from '../back-end-model-builder/back-end-model-builder.service';
import {FrontEndModelBuilderService} from '../front-end-model-builder/front-end-model-builder.service';

export interface MappingEngineState {
  mappingEngine?: MappingEngine;
  uuid?: string;
  owner?: MappingEngineOwner;
  isDefault?: boolean;
  shopSystemName?: MappingEngineShopSystem | ''; // undefined = not set yet, empty string = no plugin
  mappingEngineApiResponse?: MappingEngineGetDetailsResp;
  sourceAttributes?: string[];
  customSourceAttributes?: string[];
  destinationAttributes?: DestinationAttribute[];
  saved?: boolean;

  isLoading?: boolean;
  isBuildingWithDetails?: boolean;
}

@Injectable()
export class MappingEngineStore extends ComponentStore<MappingEngineState> {
  private readonly _mappingEngineGateway = inject(MappingEngineGateway);
  private readonly _frontEndModelBuilderService = inject(
    FrontEndModelBuilderService,
  );
  private readonly _backEndModelBuilderService = inject(
    BackEndModelBuilderService,
  );

  readonly isLoading$: Observable<boolean | undefined> = this.select(
    (state) => state.isLoading || state.isBuildingWithDetails,
  );

  readonly isDefault$: Observable<boolean | undefined> = this.select(
    (state) => state?.isDefault,
  );

  readonly uuid$: Observable<string | undefined> = this.select(
    (state) => state.uuid,
  );

  readonly owner$: Observable<MappingEngineOwner | undefined> = this.select(
    (state) => state.owner,
  );

  readonly shopSystemName$: Observable<
    MappingEngineShopSystem | '' | undefined
  > = this.select((state) => state.shopSystemName);

  readonly mappingEngine$: Observable<MappingEngine | undefined> = this.select(
    (state) => state.mappingEngine,
  );

  readonly sourceAttributes$: Observable<string[] | undefined> = this.select(
    (state) => state.sourceAttributes,
  );

  readonly customSourceAttributes$: Observable<string[] | undefined> =
    this.select((state) => state.customSourceAttributes);

  readonly saved$: Observable<boolean | undefined> = this.select(
    (state) => state.saved,
  );

  readonly destinationAttributes$: Observable<
    DestinationAttribute[] | undefined
  > = this.select((state) => state.destinationAttributes);

  private readonly _mappingEngineApiResponse$ = this.select(
    (state) => state.mappingEngineApiResponse,
  );

  constructor() {
    super({});
    this.loadMappingEngineSummary();
    this.buildMappingEngine();
  }

  readonly updateUuid = this.updater<string>(
    (state, uuid): MappingEngineState => ({
      ...state,
      uuid,
    }),
  );

  readonly updateOwner = this.updater<MappingEngineOwner>(
    (state, owner): MappingEngineState => ({
      ...state,
      owner,
    }),
  );

  // don't allow null value
  readonly updateShopSystemName = this.updater<MappingEngineShopSystem | ''>(
    (state, shopSystemName): MappingEngineState => ({
      ...state,
      shopSystemName,
    }),
  );

  readonly configureAttributes = this.updater<MappingEngineAttributes>(
    (state, attributes): MappingEngineState => {
      const destinationAttributes =
        this._frontEndModelBuilderService.buildDestinationAttributes(
          attributes.destination_attributes,
        );
      return {
        ...state,
        sourceAttributes: attributes.source_attributes,
        customSourceAttributes: attributes.custom_attributes,
        destinationAttributes: destinationAttributes.sort((att1, att2) =>
          att1.attribute >= att2.attribute ? -1 : 1,
        ),
      };
    },
  );

  readonly loadMappingEngineSummary = this.effect<void>((trigger$) =>
    trigger$.pipe(
      switchMap(() => this.uuid$),
      filter((uuid) => !!uuid),
      switchMap((uuid) => {
        this.patchState({isLoading: true});
        return this._mappingEngineGateway
          .getMappingEngine({uuid: uuid as string})
          .pipe(
            tapResponse({
              next: (apiResponse) => {
                this.patchState({
                  isDefault: apiResponse.is_default,
                  isLoading: false,
                });
                if (!apiResponse.is_default) {
                  this.loadMappingEngineDetails();
                }
              },
              error: () => {
                this.patchState({
                  isLoading: false,
                });
              },
            }),
          );
      }),
    ),
  );

  readonly loadMappingEngineDetails = this.effect<void>((trigger$) =>
    trigger$.pipe(
      switchMap(() =>
        combineLatest([this.uuid$, this.owner$, this.shopSystemName$]),
      ),
      filter(
        ([uuid, owner, shopSystemName]) =>
          !!uuid && !!owner && shopSystemName !== undefined,
      ),
      switchMap(([uuid, owner, shopSystemName]) => {
        this.patchState({isBuildingWithDetails: true});
        const postObject: MappingEngineGetReq = {
          uuid: uuid as string,
          owner: owner as MappingEngineOwner,
        };
        if (shopSystemName) {
          postObject.plugin = shopSystemName;
        }

        return this._mappingEngineGateway.getDetails(postObject).pipe(
          tapResponse({
            next: (apiResponse) => {
              this.patchState({
                mappingEngineApiResponse: apiResponse,
              });
            },
            error: () => {},
          }),
        );
      }),
    ),
  );

  readonly buildMappingEngine = this.effect<void>((trigger$) =>
    trigger$.pipe(
      switchMap(() =>
        combineLatest([
          this._mappingEngineApiResponse$,
          this.destinationAttributes$,
          this.sourceAttributes$,
        ]).pipe(
          tapResponse({
            next: ([apiResponse, destinationAttributes, sourceAttributes]) => {
              if (apiResponse && destinationAttributes && sourceAttributes) {
                this.patchState({
                  mappingEngine: this._addMissingAttributes(
                    this._frontEndModelBuilderService.buildMappingEngine(
                      apiResponse,
                      sourceAttributes,
                      destinationAttributes,
                    ),
                    destinationAttributes,
                  ),
                  uuid: apiResponse.uuid,
                  sourceAttributes,
                  destinationAttributes,
                  saved: false,
                  isBuildingWithDetails: false,
                });
              }
            },
            error: () => {},
          }),
        ),
      ),
    ),
  );

  readonly saveMappingEngine = this.effect<MappingEngine>((mappingEngine$) =>
    mappingEngine$.pipe(
      withLatestFrom(this.uuid$),
      switchMap(([mappingEngine, uuid]) => {
        this.patchState({isLoading: true});
        return this._mappingEngineGateway
          .postMappingEngine({
            uuid,
            ...this._backEndModelBuilderService.buildMappingEngine(
              mappingEngine,
            ),
          })
          .pipe(
            tapResponse({
              next: () => {
                this.patchState({
                  isLoading: false,
                  saved: true,
                });
                this.loadMappingEngineSummary();
              },
              error: () => {
                this.patchState({
                  isLoading: false,
                  saved: false,
                });
              },
            }),
          );
      }),
    ),
  );

  readonly revertToDefault = this.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.uuid$),
      switchMap(([, uuid]) => {
        this.patchState({isLoading: true});
        return this._mappingEngineGateway
          .postMappingEngine({
            uuid,
            is_default: true,
            attributes: [], // attributes always at the end, backend limitation
          })
          .pipe(
            tapResponse({
              next: () => {
                this.patchState({
                  isLoading: false,
                  saved: true,
                });
                this.loadMappingEngineSummary();
              },
              error: () => {},
            }),
          );
      }),
    ),
  );

  private _addMissingAttributes(
    mappingEngine: MappingEngine | undefined,
    destinationAttributes: DestinationAttribute[],
  ): MappingEngine | undefined {
    if (mappingEngine) {
      destinationAttributes
        .filter((attr) => attr.required)
        .forEach((attr) => {
          const existingRule = mappingEngine.mappingRuleList.find(
            (RL) => RL.attribute === attr.attribute,
          );

          if (!existingRule) {
            mappingEngine.mappingRuleList.push({
              attribute: attr.attribute,
              mappingRules: [
                {
                  mappingType: 'attribute',
                  additionalProcessor: {},
                },
              ],
              stripHTML: false,
              custom: false,
            });
          }
        });
    }
    return mappingEngine;
  }
}
